import { useState, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ko } from 'date-fns/locale';

import Alert from '@shared/components/atoms/Alert';
import dropdownIcon from '@shared/assets/images/dropdown_icon.svg';
import closeIcon from '@shared/assets/images/close_icon.svg';

import styles from './MobileFilterSheet.module.scss';

/**
 * 날짜 문자열(yyyy-MM-dd)을 Date 객체로 변환
 */
function toDate(dateString) {
  if (!dateString) return null;
  const parsed = new Date(dateString);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Date 객체를 yyyy-MM-dd 형식 문자열로 변환
 */
function formatDate(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 커스텀 DatePicker 인풋 (forwardRef 필수)
 */
const CustomDateInput = forwardRef(function CustomDateInput({ value, onClick, placeholder }, ref) {
  return (
    <button type="button" className={styles.dateButton} onClick={onClick} ref={ref}>
      <span className={value ? styles.dateValue : styles.datePlaceholder}>
        {value || placeholder}
      </span>
    </button>
  );
});

CustomDateInput.displayName = 'CustomDateInput';

/**
 * 모바일 필터 바텀시트 컴포넌트
 * - 화면 하단에서 올라오는 바텀시트 형태
 * - 필터 옵션 선택 가능
 * - Figma 디자인 기반
 */
function MobileFilterSheet({
  isOpen,
  onClose,
  title,
  filterFields,
  filterValues,
  onFilterChange,
  onApply,
  onReset,
  onSelectFieldClick,
}) {
  const [localValues, setLocalValues] = useState(filterValues);
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  // filterValues가 변경되면 localValues 업데이트
  useEffect(
    function () {
      setLocalValues(filterValues);
    },
    [filterValues],
  );

  // 바텀시트가 열릴 때 body 스크롤 방지
  useEffect(
    function () {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      return function () {
        document.body.style.overflow = '';
      };
    },
    [isOpen],
  );

  function handleInputChange(key, value) {
    setLocalValues(function (prev) {
      return { ...prev, [key]: value };
    });
  }

  function handleDateChange(key, date) {
    const formatted = formatDate(date);
    setLocalValues(function (prev) {
      return { ...prev, [key]: formatted };
    });
  }

  function handleApply() {
    const dateRangeFields = filterFields.filter((f) => f.type === 'dateRange');
    for (const field of dateRangeFields) {
      const startStr = localValues[field.startKey];
      const endStr = localValues[field.endKey];
      if (startStr && endStr) {
        const startDate = new Date(startStr);
        const endDate = new Date(endStr);
        if (
          !isNaN(startDate.getTime()) &&
          !isNaN(endDate.getTime()) &&
          startDate.getTime() > endDate.getTime()
        ) {
          setAlertState({ isOpen: true, message: '날짜를 다시 확인해 주세요.' });
          return;
        }
      }
    }

    onFilterChange(localValues);
    onApply(localValues);
    onClose();
  }

  function handleReset() {
    const resetValues = {};
    filterFields.forEach(function (field) {
      if (field.key) {
        resetValues[field.key] = '';
      }
      if (field.startKey) {
        resetValues[field.startKey] = '';
      }
      if (field.endKey) {
        resetValues[field.endKey] = '';
      }
    });
    setLocalValues(resetValues);
    onReset();
  }

  function renderField(field) {
    if (field.newRow) return null;

    switch (field.type) {
      case 'dateRange': {
        const startDate = toDate(localValues[field.startKey]);
        const endDate = toDate(localValues[field.endKey]);

        return (
          <div key={field.key} className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{field.label}</label>
            <div className={styles.dateRangeWrapper}>
              <div className={styles.selectBox}>
                <div className={styles.dateInputWrapper}>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => handleDateChange(field.startKey, date)}
                    locale={ko}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={field.startPlaceholder || '날짜 선택'}
                    customInput={
                      <CustomDateInput placeholder={field.startPlaceholder || '날짜 선택'} />
                    }
                    maxDate={endDate}
                    withPortal
                    portalId="mobile-datepicker-portal"
                  />
                  <img src={dropdownIcon} alt="" className={styles.dateIcon} />
                </div>
                <div className={styles.selectDivider} />
              </div>
              <span className={styles.dateSeparator}>~</span>
              <div className={styles.selectBox}>
                <div className={styles.dateInputWrapper}>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => handleDateChange(field.endKey, date)}
                    locale={ko}
                    dateFormat="yyyy-MM-dd"
                    placeholderText={field.endPlaceholder || '날짜 선택'}
                    customInput={
                      <CustomDateInput placeholder={field.endPlaceholder || '날짜 선택'} />
                    }
                    minDate={startDate}
                    withPortal
                    portalId="mobile-datepicker-portal"
                  />
                  <img src={dropdownIcon} alt="" className={styles.dateIcon} />
                </div>
                <div className={styles.selectDivider} />
              </div>
            </div>
          </div>
        );
      }

      case 'select':
        return (
          <div key={field.key} className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{field.label}</label>
            <button
              type="button"
              className={styles.selectButton}
              onClick={() => onSelectFieldClick(field, localValues[field.key])}
            >
              <span className={styles.selectValue}>
                {field.options?.find((opt) => opt.value === localValues[field.key])?.label ||
                  '전체'}
              </span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#828282"
                  strokeWidth="1.33"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div className={styles.selectDivider} />
          </div>
        );

      case 'input': {
        // 최대 글자수 (기본값 200)
        const maxLength = field.maxLength || 200;

        return (
          <div key={field.key} className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{field.label}</label>
            <div className={styles.inputWrapper}>
              <div className={styles.inputContainer}>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder={field.placeholder}
                  value={localValues[field.key] || ''}
                  maxLength={maxLength}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                />
                {localValues[field.key] && (
                  <button
                    type="button"
                    className={styles.clearButton}
                    onClick={() => handleInputChange(field.key, '')}
                    aria-label="입력 초기화"
                  >
                    <img src={closeIcon} alt="삭제" width={14} height={14} />
                  </button>
                )}
              </div>
              <div className={styles.selectDivider} />
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  }

  if (!isOpen) return null;

  // iOS WebView에서 position: fixed가 제대로 작동하도록 body에 직접 렌더링
  const content = (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
        {/* 헤더 */}
        <header className={styles.header}>
          <h3 className={styles.headerTitle}>{title}</h3>
          <button type="button" className={styles.resetButton} onClick={handleReset}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.1 4.09998C10.1 4.09998 9.09748 2.73409 8.28306 1.9191C7.46863 1.10411 6.34318 0.599976 5.09998 0.599976C2.61469 0.599976 0.599976 2.61469 0.599976 5.09998C0.599976 7.58526 2.61469 9.59998 5.09998 9.59998C7.15152 9.59998 8.88242 8.22712 9.42409 6.34998M10.1 4.09998V1.09998M10.1 4.09998H7.09998"
                stroke="#424242"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>초기화</span>
          </button>
        </header>

        {/* 필터 콘텐츠 */}
        <div className={styles.content}>
          {filterFields.map(function (field) {
            return renderField(field);
          })}
        </div>

        {/* 하단 버튼 */}
        <footer className={styles.footer}>
          <button type="button" className={styles.applyButton} onClick={handleApply}>
            적용
          </button>
        </footer>
      </div>

      {/* DatePicker 포탈 */}
      <div id="mobile-datepicker-portal" />

      <Alert
        isOpen={alertState.isOpen}
        message={alertState.message}
        onClose={() => setAlertState({ isOpen: false, message: '' })}
      />
    </div>
  );

  return createPortal(content, document.body);
}

MobileFilterSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  filterFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      type: PropTypes.string,
      label: PropTypes.string,
      options: PropTypes.array,
      placeholder: PropTypes.string,
      startKey: PropTypes.string,
      endKey: PropTypes.string,
      newRow: PropTypes.bool,
    }),
  ),
  filterValues: PropTypes.object,
  onFilterChange: PropTypes.func,
  onApply: PropTypes.func,
  onReset: PropTypes.func,
  onSelectFieldClick: PropTypes.func,
};

MobileFilterSheet.defaultProps = {
  title: '필터',
  filterFields: [],
  filterValues: {},
  onFilterChange: () => {},
  onApply: () => {},
  onReset: () => {},
  onSelectFieldClick: () => {},
};

export default MobileFilterSheet;
